import React, { useRef } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { createYoutubeAutoLinkMatcher } from "../../utils/youtubePreview.util";
import CustomToolbar from "./CustomToolbat";
import useYoutubeAutoLinkPaste from "./useYoutubeAutoLinkPaste";
import useStickyQuillFont from "./useStickyQuillFont";
import useYoutubeThumbnailHover from "./useYoutubeThumbnailHover";
import YoutubeThumbnailPreview from "./YoutubeThumbnailPreview";
import FileTransferPromoOverlay from "../../features/fileTransferPromo/FileTransferPromoOverlay";
import { FILE_TRANSFER_PROMO_PHASES } from "../../features/fileTransferPromo/fileTransferPromo.constants";

const Delta = Quill.import("delta");
const Font = Quill.import("formats/font");
const QUILL_FONT_WHITELIST = [
  "normal",
  "sans-serif",
  "serif",
  "monospace",
  "palisade",
];
const FONT_FORMAT_NAME = "font";
const NEWLINE_TEXT = "\n";
const QUILL_BLOCK_FORMATS = [
  "align",
  "blockquote",
  "code-block",
  "direction",
  "header",
  "indent",
  "list",
  "table",
];
const TEXT_NODE_FALLBACK = 3;
const TEXT_NODE_SELECTOR =
  typeof Node === "undefined" ? TEXT_NODE_FALLBACK : Node.TEXT_NODE;
const QUILL_SILENT_SOURCE = Quill.sources.SILENT;
const QUILL_USER_SOURCE = Quill.sources.USER;
const YOUTUBE_AUTO_LINK_MATCHER = createYoutubeAutoLinkMatcher(Delta);
const EDITOR_PLACEHOLDER = [
  "Write something legendary 🖋️.......",
  "Tips: ",
  "- You can also use ctrl/cmd + s to save your note.",
  "- Add # to title and store notes on-device (completely private)",
  "- New Interface & features on the way 🚀",
].join("\n\n");

Font.whitelist = QUILL_FONT_WHITELIST;
Quill.register(Font, true);

const getLineFormats = (formats) =>
  QUILL_BLOCK_FORMATS.reduce((lineFormats, formatName) => {
    if (formats[formatName] == null) return lineFormats;

    return {
      ...lineFormats,
      [formatName]: formats[formatName],
    };
  }, {});

const insertNewlineWithCurrentFont = function (range, context) {
  const fontValue = context.format[FONT_FORMAT_NAME];
  const lineFormats = getLineFormats(context.format);
  const delta = new Delta()
    .retain(range.index)
    .delete(range.length)
    .insert(NEWLINE_TEXT, lineFormats);

  this.quill.updateContents(delta, QUILL_USER_SOURCE);
  this.quill.setSelection(
    range.index + NEWLINE_TEXT.length,
    QUILL_SILENT_SOURCE
  );
  if (fontValue) {
    this.quill.format(FONT_FORMAT_NAME, fontValue, QUILL_USER_SOURCE);
  }
  this.quill.focus();
};

const getEditorClassName = (isLoading) =>
  `font-montserrat h-[60vh] ${isLoading ? "animateQuill" : ""}`;

const RichTextEditor = ({
  fileTransferPromo,
  loading,
  onChange,
  quillRef,
  value,
}) => (
  <div className="relative">
    <ReactQuill
      ref={quillRef}
      className={getEditorClassName(loading)}
      theme="snow"
      value={value}
      onChange={onChange}
      onFocus={fileTransferPromo.hidePromo}
      modules={QuillComponent.modules}
      formats={QuillComponent.formats}
      placeholder={EDITOR_PLACEHOLDER}
    />
    <FileTransferPromoOverlay
      phase={fileTransferPromo.phase}
      isVisible={fileTransferPromo.isVisible}
    />
  </div>
);

const QuillComponent = ({
  autoSaveCheckIntervalMs,
  isAutoSaveEnabled = false,
  isAutoSaveSaving = false,
  value,
  onChange,
  loading = false,
  onToggleAutoSave = () => {},
  fileTransferPromo = {
    hidePromo: () => {},
    isVisible: false,
    phase: FILE_TRANSFER_PROMO_PHASES.HIDDEN,
  },
}) => {
  const quillRef = useRef(null);
  const { hidePreview, previewState } = useYoutubeThumbnailHover(quillRef);
  useYoutubeAutoLinkPaste({
    Delta,
    quillRef,
    silentSource: QUILL_SILENT_SOURCE,
    userSource: QUILL_USER_SOURCE,
  });
  useStickyQuillFont({
    Delta,
    quillRef,
    silentSource: QUILL_SILENT_SOURCE,
    userSource: QUILL_USER_SOURCE,
  });

  return (
    <div>
      <CustomToolbar
        autoSaveCheckIntervalMs={autoSaveCheckIntervalMs}
        isAutoSaveEnabled={isAutoSaveEnabled}
        isAutoSaveSaving={isAutoSaveSaving}
        onToggleAutoSave={onToggleAutoSave}
      />
      <RichTextEditor
        fileTransferPromo={fileTransferPromo}
        loading={loading}
        onChange={onChange}
        quillRef={quillRef}
        value={value}
      />
      <YoutubeThumbnailPreview
        isVisible={previewState.isVisible}
        onImageError={hidePreview}
        position={previewState.position}
        thumbnailUrl={previewState.thumbnailUrl}
      />
    </div>
  );
};

QuillComponent.modules = {
  toolbar: {
    container: "#toolbar",
  },
  keyboard: {
    bindings: {
      "font enter": {
        key: "Enter",
        shiftKey: null,
        handler: insertNewlineWithCurrentFont,
      },
    },
  },
  clipboard: {
    matchVisual: false,
    matchers: [[TEXT_NODE_SELECTOR, YOUTUBE_AUTO_LINK_MATCHER]],
  },
};

QuillComponent.formats = [
  "header",
  "font",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "code-block",
  "align",
  "direction",
  "video",
];

export default QuillComponent;
